import {
  getClientPreferenceOptions,
  type ClientPreferenceOption,
  type ClientPreferenceOptions,
} from '@/lib/api/clientPreferenceOptions';
import {
  VALID_ALLERGIES as FALLBACK_ALLERGIES,
  VALID_CUISINE_TYPES as FALLBACK_CUISINES,
  VALID_FOOD_INTOLERANCES as FALLBACK_INTOLERANCES,
  VALID_PREFERENCES as FALLBACK_PREFERENCES,
} from '@/constants/dietary-goals';

// Bump when the canonical catalog changes so existing users do not retain stale dropdown values.
const STORAGE_KEY = 'cs_client_preference_options_v2';
/** Revalidate at most once per day; otherwise reuse stored options. */
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

type PreferenceOptionLike = { name: string; value: string; id?: string; parentId?: string };

type StoredPreferenceOptions = {
  savedAt: number;
  data: ClientPreferenceOptions;
};

let memoryCache: ClientPreferenceOptions | null = null;
let inFlight: Promise<ClientPreferenceOptions> | null = null;

const toPreferenceOption = (item: PreferenceOptionLike): ClientPreferenceOption => ({
  id: item.id?.trim() || item.value,
  name: item.name,
  value: item.value,
  ...(item.parentId ? { parentId: item.parentId } : {}),
});

const fallbackOptions = (): ClientPreferenceOptions => ({
  allergies: FALLBACK_ALLERGIES.map(toPreferenceOption),
  foodIntolerances: FALLBACK_INTOLERANCES.map(toPreferenceOption),
  dietaryPreferences: FALLBACK_PREFERENCES.map(toPreferenceOption),
  cuisineTypes: FALLBACK_CUISINES.map(toPreferenceOption),
});

const isUsableOptions = (data: ClientPreferenceOptions | null | undefined): data is ClientPreferenceOptions =>
  Boolean(
    data &&
      (data.allergies.length > 0 ||
        data.foodIntolerances.length > 0 ||
        data.dietaryPreferences.length > 0 ||
        data.cuisineTypes.length > 0),
  );

const readPersistedCache = (): ClientPreferenceOptions | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredPreferenceOptions;
    if (!parsed?.savedAt || !isUsableOptions(parsed.data)) return null;
    if (Date.now() - parsed.savedAt > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
};

const writePersistedCache = (data: ClientPreferenceOptions): void => {
  if (typeof window === 'undefined') return;
  try {
    const payload: StoredPreferenceOptions = { savedAt: Date.now(), data };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore quota / private-mode failures; memory cache still works.
  }
};

const readCache = (): ClientPreferenceOptions | null => {
  if (memoryCache && isUsableOptions(memoryCache)) return memoryCache;
  const persisted = readPersistedCache();
  if (persisted) {
    memoryCache = persisted;
    return persisted;
  }
  return null;
};

export const getCachedClientPreferenceOptions = (): ClientPreferenceOptions | null => readCache();

export const getAllergyOptions = (): ClientPreferenceOption[] =>
  readCache()?.allergies ?? fallbackOptions().allergies;

export const getFoodIntoleranceOptions = (): ClientPreferenceOption[] =>
  readCache()?.foodIntolerances ?? fallbackOptions().foodIntolerances;

export const getDietaryPreferenceOptions = (): ClientPreferenceOption[] =>
  readCache()?.dietaryPreferences ?? fallbackOptions().dietaryPreferences;

export const getCuisineTypeOptions = (): ClientPreferenceOption[] =>
  readCache()?.cuisineTypes ?? fallbackOptions().cuisineTypes;

/** Name/value shape used by most existing UI helpers. */
export const getValidAllergies = (): Array<{ name: string; value: string }> =>
  getAllergyOptions().map(({ name, value }) => ({ name, value }));

export const getValidFoodIntolerances = (): Array<{ name: string; value: string }> =>
  getFoodIntoleranceOptions().map(({ name, value }) => ({ name, value }));

export const getValidPreferences = (): Array<{ name: string; value: string }> =>
  getDietaryPreferenceOptions().map(({ name, value }) => ({ name, value }));

export const getValidCuisineTypes = (): Array<{ name: string; value: string; parentId?: string }> =>
  getCuisineTypeOptions().map(({ name, value, parentId }) => ({
    name,
    value,
    ...(parentId ? { parentId } : {}),
  }));

/** Recipe intolerance tags — same catalog as food intolerances from preference-options. */
export const getRecipeIntoleranceTags = (): Array<{ name: string; value: string }> =>
  getValidFoodIntolerances();

/**
 * Load preference options once and reuse from memory/localStorage.
 * Pass `force: true` to bypass cache (e.g. after admin catalog changes).
 */
export const ensureClientPreferenceOptions = async (
  options?: { force?: boolean },
): Promise<ClientPreferenceOptions> => {
  if (!options?.force) {
    const cached = readCache();
    if (cached) return cached;
  }

  if (inFlight && !options?.force) return inFlight;

  inFlight = (async () => {
    try {
      const data = await getClientPreferenceOptions();
      if (!isUsableOptions(data)) {
        const fallback = fallbackOptions();
        memoryCache = fallback;
        return fallback;
      }
      memoryCache = data;
      writePersistedCache(data);
      return data;
    } catch {
      const cached = readCache();
      if (cached) return cached;
      const fallback = fallbackOptions();
      memoryCache = fallback;
      return fallback;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
};

export const clearClientPreferenceOptionsCache = (): void => {
  memoryCache = null;
  inFlight = null;
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

export type { ClientPreferenceOption, ClientPreferenceOptions };
